/* ==========================================================================
   Cursor pill — one global controller for every trigger on the page.
   Creates a single pill in <body> and tracks the pointer through delegated
   pointerover/pointerout, so anything opts in with markup only:

     class="cursor-pill-trigger"            → label from aria-label / title
     data-cursor-label="View Project"       → explicit label (works alone too)
     data-cursor-theme="dark"               → dark pill variant

   Mouse-only (hover + fine pointer); no-ops elsewhere.
   ========================================================================== */
(function () {
  'use strict';

  var SELECTOR = '.cursor-pill-trigger, [data-cursor-label]';
  /* Labels come from article/block titles, which can run to a full headline.
     The pill sizes itself to its content, so an untruncated label would drag a
     ~700px box around behind the pointer. The cap has to clear the longest
     intentional label — "View Case Study Los Angeles Archive" is 35 chars — or
     it would truncate away the very name the label exists to show. */
  var MAX_LABEL = 40;
  var pill = null;
  var label = null;
  var arrow = null;
  var current = null;

  function supported() {
    return !!(window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches);
  }

  function build() {
    if (pill) return pill;

    pill = document.createElement('div');
    pill.className = 'cursor-pill';
    pill.setAttribute('aria-hidden', 'true');

    var content = document.createElement('span');
    content.className = 'cursor-pill__content';

    label = document.createElement('span');
    label.className = 'cursor-pill__label';

    arrow = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    arrow.setAttribute('class', 'cursor-pill__arrow');
    arrow.setAttribute('width', '8');
    arrow.setAttribute('height', '8');
    arrow.setAttribute('viewBox', '0 0 8 8');
    arrow.setAttribute('fill', 'none');
    arrow.setAttribute('focusable', 'false');
    arrow.setAttribute('aria-hidden', 'true');
    arrow.innerHTML =
      '<path d="M0.355469 0.5H7.35737V7.58404" stroke="currentColor" stroke-miterlimit="10"/>' +
      '<path d="M7.35737 0.5L0.355469 7.58404" stroke="currentColor" stroke-miterlimit="10"/>';

    content.appendChild(label);
    content.appendChild(arrow);
    pill.appendChild(content);
    document.body.appendChild(pill);

    return pill;
  }

  /* Width of label + arrow, measured off the children: the content box still
     carries the previous (or in-flight) track width when the pointer moves
     straight from one trigger to the next. */
  function contentWidth() {
    var width = label.getBoundingClientRect().width;
    var styles = window.getComputedStyle(arrow);
    width += arrow.getBoundingClientRect().width + (parseFloat(styles.marginLeft) || 0);
    return Math.ceil(width);
  }

  function moveTo(event) {
    pill.style.translate =
      'calc(-50% + ' + event.clientX + 'px) calc(-50% + ' + event.clientY + 'px)';
  }

  /* Trim on a word boundary when there is one worth keeping, so the pill reads
     as a name rather than a cut-off sentence. */
  function truncate(text) {
    text = String(text).trim().replace(/\s+/g, ' ');
    if (text.length <= MAX_LABEL) return text;

    var cut = text.slice(0, MAX_LABEL);
    var space = cut.lastIndexOf(' ');
    if (space > 12) cut = cut.slice(0, space);
    return cut.replace(/[\s.,:;–—-]+$/, '') + '…';
  }

  /* Explicit label wins; class-only triggers fall back to their accessible
     name so `class="cursor-pill-trigger"` alone is enough. */
  function labelFor(trigger) {
    return truncate(
      trigger.getAttribute('data-cursor-label') ||
        trigger.getAttribute('aria-label') ||
        trigger.getAttribute('title') ||
        'View'
    );
  }

  function open(trigger, event) {
    build();
    current = trigger;
    label.textContent = labelFor(trigger);
    pill.classList.toggle('cursor-pill--dark', trigger.getAttribute('data-cursor-theme') === 'dark');
    moveTo(event);
    pill.style.gridTemplateColumns = contentWidth() + 'px';
    pill.classList.add('is-active');
  }

  function close() {
    current = null;
    if (!pill) return;
    pill.classList.remove('is-active');
    pill.style.gridTemplateColumns = '0px';
  }

  function onOver(event) {
    var trigger = event.target.closest ? event.target.closest(SELECTOR) : null;
    if (!trigger) {
      if (current) close();
      return;
    }
    if (event.pointerType && event.pointerType !== 'mouse') return;
    if (trigger !== current) open(trigger, event);
    else moveTo(event);
  }

  function onOut(event) {
    if (!current) return;
    // ignore moves between descendants of the same trigger
    var to = event.relatedTarget;
    if (to && current.contains(to)) return;
    close();
  }

  function boot() {
    if (!supported()) return;
    document.addEventListener('pointerover', onOver, { passive: true });
    document.addEventListener('pointermove', onOver, { passive: true });
    document.addEventListener('pointerout', onOut, { passive: true });
    document.addEventListener('pointercancel', close, { passive: true });
    window.addEventListener('blur', close);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
